import {
  signal,
  type UseMutation$Options,
  useSignalValue,
  useSwitchMutation$,
} from "reactjrx"
import { catchError, combineLatest, defer, from, map, mergeMap } from "rxjs"
import { GapiNotAvailableError } from "./errors"
import { retryOnFailure } from "../../../../common/network/retryOnFailure"
import { loadScript } from "../../../../common/scripts"
import { useConfig } from "../../../../config/useConfig"

const ID = "oboku-google-api-script"

export const gapiSignal = signal<
  | {
      gapi: undefined
      state: "loading"
      error?: string
      loadingStep: undefined | "api" | "client"
    }
  | { gapi: typeof gapi; state: "loaded"; error?: string }
>({
  default: {
    gapi: undefined,
    state: "loading",
    error: undefined,
    loadingStep: undefined,
  },
})

export const gapi$ = gapiSignal.pipe(map(({ gapi }) => gapi))

export const gapiOrThrow$ = gapi$.pipe(
  map((gapi) => {
    if (!gapi) {
      throw new GapiNotAvailableError()
    }

    return gapi
  }),
)

export const useGapi = () => {
  return useSignalValue(gapiSignal).gapi
}

const loadGapiClient = () =>
  defer(() =>
    from(
      new Promise((resolve, reject) => {
        window.gapi.load("client", {
          callback: resolve,
          onerror: reject,
        })
      }),
    ),
  )

const loadPicker = () =>
  defer(() =>
    from(
      new Promise((resolve, reject) => {
        window.gapi.load("picker", {
          callback: resolve,
          onerror: reject,
        })
      }),
    ),
  )

const initClient = (apiKey: string | undefined) =>
  defer(() =>
    from(
      window.gapi.client.init({
        apiKey,
        discoveryDocs: [
          "https://www.googleapis.com/discovery/v1/apis/drive/v3/rest",
        ],
      }),
    ),
  )

export const useLoadGapi = (
  options?: Pick<UseMutation$Options<unknown>, "meta">,
) => {
  const { data: config } = useConfig()

  return useSwitchMutation$({
    mutationKey: ["pluginGoogleScriptMutation"],
    ...options,
    mutationFn: () => {
      return loadScript({
        id: ID,
        src: "https://apis.google.com/js/api.js",
      }).pipe(
        catchError((error) => {
          gapiSignal.update((state) => ({
            ...state,
            error: "Error while loading script",
          }))

          throw error
        }),
        retryOnFailure,
        mergeMap(() =>
          combineLatest([
            loadGapiClient().pipe(
              catchError((error) => {
                gapiSignal.update((state) => ({
                  ...state,
                  error: "Error while loading client script",
                }))

                throw error
              }),
              retryOnFailure,
            ),
            loadPicker().pipe(
              catchError((error) => {
                gapiSignal.update((state) => ({
                  ...state,
                  error: "Error while loading picker script",
                }))

                throw error
              }),
              retryOnFailure,
            ),
          ]).pipe(
            mergeMap(() =>
              initClient(config?.GOOGLE_API_KEY).pipe(
                catchError((error) => {
                  gapiSignal.update((state) => ({
                    ...state,
                    error: "Error while initializing the client",
                  }))

                  throw error
                }),
                retryOnFailure,
              ),
            ),
            map(() => {
              gapiSignal.update({
                gapi: window.gapi,
                state: "loaded",
                error: undefined,
              })

              return gapiSignal.getValue()
            }),
          ),
        ),
      )
    },
  })
}
